/**
 * Microsoft Graph helpers for the Documents tab — LIVE browsing of the
 * project's SharePoint Job Folder with the signed-in user's delegated token.
 *
 * SharePoint is the source of truth: the tab is a window into the real
 * folder (including files added outside PMT), never a database mirror.
 * Same /shares resolution + consent handling as graphUpload.ts.
 */
import { GraphConsentError, resolveFolder } from './graphUpload';

const GRAPH = 'https://graph.microsoft.com/v1.0';

/** One entry (file or folder) inside the Job Folder. */
export interface DriveEntry {
  id: string;
  name: string;
  isFolder: boolean;
  /** Folder only: number of children (SharePoint's count). */
  childCount: number | null;
  /** File only: bytes. */
  size: number | null;
  webUrl: string;
  lastModifiedBy: string | null;
  lastModifiedDateTime: string | null;
}

export interface JobFolderHandle {
  driveId: string;
  folderId: string;
}

async function acquire(getToken: () => Promise<string>): Promise<string> {
  let token: string;
  try {
    token = await getToken();
  } catch (e) {
    if (e instanceof GraphConsentError) throw e;
    throw new GraphConsentError(e instanceof Error ? e.message : 'Graph consent required');
  }
  if (!token) throw new GraphConsentError('No Graph access token was returned.');
  return token;
}

async function graphError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    const msg = body?.error?.message ?? body?.message;
    if (msg) return `${fallback}: ${msg}`;
  } catch { /* non-JSON */ }
  return `${fallback} (HTTP ${res.status})`;
}

/** Resolve the project's job_folder_url → drive + folder ids. */
export async function resolveJobFolder(
  getToken: () => Promise<string>,
  jobFolderUrl: string,
): Promise<JobFolderHandle> {
  const token = await acquire(getToken);
  return resolveFolder(token, jobFolderUrl);
}

function mapEntry(c: any): DriveEntry {
  return {
    id: c.id,
    name: c.name ?? '(unnamed)',
    isFolder: c.folder != null,
    childCount: c.folder?.childCount ?? null,
    size: c.file != null ? (c.size ?? null) : null,
    webUrl: c.webUrl,
    lastModifiedBy: c.lastModifiedBy?.user?.displayName ?? null,
    lastModifiedDateTime: c.lastModifiedDateTime ?? null,
  };
}

/** List a folder's children (folders first, then files, both A→Z). Pages
 *  through @odata.nextLink so big folders come back whole. */
export async function listChildren(
  getToken: () => Promise<string>,
  driveId: string,
  itemId: string,
): Promise<DriveEntry[]> {
  const token = await acquire(getToken);
  const out: DriveEntry[] = [];
  let url =
    `${GRAPH}/drives/${driveId}/items/${itemId}/children` +
    `?$select=id,name,size,folder,file,webUrl,lastModifiedBy,lastModifiedDateTime&$top=500`;
  while (url) {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(await graphError(res, "Couldn't read the folder"));
    const body = await res.json();
    for (const c of body?.value ?? []) out.push(mapEntry(c));
    url = body?.['@odata.nextLink'] ?? '';
  }
  return out.sort((a, b) =>
    a.isFolder !== b.isFolder ? (a.isFolder ? -1 : 1) : a.name.localeCompare(b.name),
  );
}

/** Create folders (skipping ones that already exist / racing creates). */
export async function createFolders(
  getToken: () => Promise<string>,
  driveId: string,
  parentId: string,
  names: string[],
): Promise<{ created: string[]; failed: { name: string; error: string }[] }> {
  const token = await acquire(getToken);
  const created: string[] = [];
  const failed: { name: string; error: string }[] = [];
  for (const name of names) {
    const res = await fetch(`${GRAPH}/drives/${driveId}/items/${parentId}/children`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }),
    });
    if (res.ok || res.status === 409) created.push(name);
    else failed.push({ name, error: await graphError(res, 'Create failed') });
  }
  return { created, failed };
}

/** Create ONE folder and return its id + webUrl. Existing folder of the same
 *  name (or a create race) resolves to that folder instead of failing — the
 *  Create-Job-Folder flow must be idempotent (someone may have made the
 *  folder in SharePoint already). */
export async function createFolder(
  getToken: () => Promise<string>,
  driveId: string,
  parentId: string,
  name: string,
): Promise<{ id: string; webUrl: string; existed: boolean }> {
  const token = await acquire(getToken);
  const res = await fetch(`${GRAPH}/drives/${driveId}/items/${parentId}/children`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }),
  });
  if (res.ok) {
    const item = await res.json();
    return { id: item.id, webUrl: item.webUrl, existed: false };
  }
  if (res.status === 409) {
    const children = await listChildren(getToken, driveId, parentId);
    const hit = children.find((c) => c.isFolder && c.name.trim().toLowerCase() === name.trim().toLowerCase());
    if (hit) return { id: hit.id, webUrl: hit.webUrl, existed: true };
  }
  throw new Error(await graphError(res, `Couldn't create the "${name}" folder`));
}

/** mp_config key holding the admin-editable standard Job Folder list — one
 *  folder name per line, edited in Admin → Config. */
export const STANDARD_FOLDERS_CONFIG_KEY = 'standardJobFolders';

/** The design handoff's standard Job Folder taxonomy (suggested, not enforced —
 *  real folders vary; the setup checklist lets the PM skip equivalents).
 *  ⚠️ This is the SEED + safety net ONLY. What the app actually offers comes
 *  from the mp_config row above — see parseStandardFolders. Adding a folder
 *  here changes nothing on an existing database, where the row already exists;
 *  add it in Admin → Config (and here, so fresh installs match). */
export const DEFAULT_STANDARD_FOLDERS = [
  'Contracts & Bonds',
  'Permits & Approvals',
  'Specifications',
  'Geotech & Survey',
  'Safety Plans',
  'Meeting Minutes',
  'Working Papers',
  'Submittal Records',
  'Closeout & O&M',
];

/** Subfolders PMT itself writes into (ensureSubfolderByPath creates them on first
 *  upload regardless) — pre-created so a new Job Folder is complete from day
 *  one and nobody hand-creates a near-miss name first. MUST match the
 *  subfolder constants in FileAttachments / SitePhotoComposer (+ Drawings soon).
 *  ⚠️ NOT admin-editable, unlike the standard list: renaming one of these in
 *  config would not rename the constant the uploader writes to, so uploads
 *  would quietly land in a second, near-miss folder. */
export const APP_FOLDERS = [
  'RFIs',
  'Submittals',
  'Daily Log Photos',
  'Drawings',
];

/** Characters SharePoint/OneDrive reject in an item name. A colon is the one
 *  that bites: it fails the UPLOAD confusingly rather than the folder create. */
const ILLEGAL_FOLDER_CHARS = /["*:<>?/\\|]/;

/** Reserved leaf names SharePoint refuses outright. */
const RESERVED_FOLDER_NAMES = new Set(['.lock', 'con', 'prn', 'aux', 'nul', 'desktop.ini']);

/** The DOS device names SharePoint reserves alongside the set above: COM0-COM9
 *  and LPT0-LPT9. A regex rather than twenty more Set entries. */
const RESERVED_DEVICE_NAME = /^(com|lpt)\d$/i;

/** Validate one folder name the way SharePoint will. Returns null when fine,
 *  otherwise the message to show the admin — checked BEFORE saving so a bad
 *  name can't sit in config until some PM's folder create fails on it. */
export function folderNameError(name: string): string | null {
  const t = name.trim();
  if (!t) return 'Enter a folder name.';
  if (t.length > 100) return 'Too long — 100 characters max.';
  if (ILLEGAL_FOLDER_CHARS.test(t)) return 'SharePoint does not allow " * : < > ? / \\ or | in a folder name.';
  if (t.startsWith('~') || t.startsWith('.') || t.endsWith('.')) {
    return 'A folder name cannot start with ~ or . or end with a period.';
  }
  if (
    RESERVED_FOLDER_NAMES.has(t.toLowerCase())
    || RESERVED_DEVICE_NAME.test(t)
    || t.toLowerCase().startsWith('_vti_')
  ) {
    return `"${t}" is a name SharePoint reserves.`;
  }
  return null;
}

/** True when `name` is one PMT's own uploaders manage. */
export const isAppFolder = (name: string): boolean =>
  APP_FOLDERS.some((a) => a.toLowerCase() === name.trim().toLowerCase());

/**
 * null when `name` is usable as a STANDARD folder specifically: a valid
 * SharePoint name that does not collide with one the app manages itself.
 *
 * ⚠️ An app-managed name in the standard list is not harmless. Both lists are
 * concatenated to build the setup checklist, so the folder would render twice
 * (two rows sharing one checkbox) and be created twice, while presenting an
 * app-managed folder as though renaming it in config would do anything.
 */
export function standardFolderEntryError(name: string): string | null {
  const problem = folderNameError(name);
  if (problem) return problem;
  if (isAppFolder(name)) {
    return `"${name.trim()}" is created and managed by the tracker itself — it is always `
      + 'added to a Job Folder, so it cannot also be a standard folder.';
  }
  return null;
}

/**
 * Parse the admin-edited config value into the folder list: one name per line,
 * blanks dropped, case-insensitive duplicates collapsed (SharePoint folder
 * names are case-insensitive, so two spellings would fight over one folder).
 *
 * ⚠️ An ABSENT value (null/undefined — no config row, which is exactly what a
 * drifted dev database looks like) falls back to the compiled defaults, so a
 * missing row can never leave the setup checklist blank. An EMPTY STRING is
 * the admin deliberately clearing the list and is honored as empty — the two
 * are not the same thing.
 */
export function parseStandardFolders(raw: string | null | undefined): string[] {
  if (raw == null) return [...DEFAULT_STANDARD_FOLDERS];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const name = line.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/** The inverse — what the admin editor writes back to config. */
export function serializeStandardFolders(names: string[]): string {
  return names.map((n) => n.trim()).filter(Boolean).join('\n');
}

/** Does `name` already exist in the Job Folder? Case-insensitive, space-tolerant
 *  — SharePoint folds both, so "safety plans " is the same folder. */
export const folderExists = (existing: string[], name: string): boolean =>
  existing.some((e) => e.trim().toLowerCase() === name.trim().toLowerCase());

/**
 * The setup checklist's default for one folder: create it unless it is already
 * there. `overrides` holds only what the PM explicitly toggled.
 *
 * ⚠️ Derived, never snapshotted. Seeding a checked-map once at mount looked
 * equivalent but was not: the configured taxonomy can arrive AFTER the modal
 * opens (a retried config fetch), and any folder missing from the seed then
 * rendered unchecked and was dropped from the create.
 */
export const isFolderChecked = (
  existing: string[],
  overrides: Record<string, boolean>,
  name: string,
): boolean => overrides[name] ?? !folderExists(existing, name);

/** What "Create N folders" will actually create — everything checked that is
 *  not already in the folder. */
export function foldersToCreate(
  all: string[],
  existing: string[],
  overrides: Record<string, boolean>,
): string[] {
  return all.filter((n) => isFolderChecked(existing, overrides, n) && !folderExists(existing, n));
}
